import { useState, useEffect } from 'react';
import {
  FaServer, FaSearch, FaCheckCircle, FaTimesCircle, FaExclamationTriangle,
  FaList, FaMapMarkerAlt, FaWifi, FaKey, FaEye, FaEyeSlash, FaCopy,
  FaChevronRight, FaRedo, FaInfoCircle, FaUser, FaTimes,
  FaExchangeAlt, FaPlusCircle, FaNetworkWired, FaArrowLeft, FaShieldAlt, FaChevronDown
} from 'react-icons/fa';
import { API_ENDPOINTS } from '../../config/api';
import AddressAutocomplete from './shared/AddressAutocomplete';

// ─── Constants ───────────────────────────────────────────────────────────────

const AU_STATES = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'ACT', 'NT'];
const STREET_TYPES = ['ST', 'RD', 'AVE', 'PL', 'CT', 'DR', 'CRES', 'BLVD', 'PDE', 'LN', 'WAY', 'HWY', 'TCE', 'CL', 'CCT', 'GR', 'ESP', 'SQ'];

// Vocus FIBRE PlanIDs, mirroring VOCUS_PLAN_MATRIX in src/services/vocusBridge.js — the
// resolver runs server-side, but the form shows the resolved PlanID so an admin can check it
// against the Vocus portal before submitting. Keep the two in sync.
const VOCUS_PLAN_MATRIX = {
  FTTP: {
    '12/1': '5933-DM', '25/5': '5935-DM', '25/10': '5936-DM', '50/20': '5937-DM',
    '100/40': '5939-DM', 'Home Fast': '6717-DM', 'Home Super Fast': '6722-DM',
    '250/100': '6862-DM', '500/200': '6864-DM', '1000/400': '6865-DM',
    'Home Ultra Fast': '6869-DM', 'Home Hyper Fast': '7013-DM', '2000/500': '7014-DM',
  },
  FTTB: { '12/1': '5940-DM', '25/5': '5941-DM', '25/10': '5942-DM', '50/20': '5943-DM', '100/40': '5944-DM', 'Home Fast': '6718-DM' },
  FTTN: { '12/1': '5945-DM', '25/10': '5946-DM', '25/5': '5947-DM', '50/20': '5948-DM', '100/40': '5949-DM', 'Home Fast': '6720-DM' },
  FTTC: { '12/1': '6300-DM', '25/5': '6301-DM', '25/10': '6302-DM', '50/20': '6303-DM', '100/40': '6304-DM', 'Home Fast': '6719-DM' },
  HFC:  {
    '12/1': '5950-DM', '25/5': '5951-DM', '25/10': '5952-DM', '50/20': '5953-DM',
    '100/40': '5954-DM', 'Home Fast': '6721-DM', 'Home Super Fast': '6723-DM',
    '250/100': '7027-DM', 'Home Ultra Fast': '6863-DM', 'Home Hyper Fast': '7012-DM',
  },
  WIRELESS: { '12/1': '5932-DM', '25/5': '5934-DM', 'Fixed Wireless Plus': '5938-DM', 'Home Fast': '6999-DM', 'Home Super Fast': '7000-DM' },
};

// Speeds behind the NAMED tiers, taken verbatim from the Vocus portal's plan dropdown.
// These are the post-Sept-2025 NBN speeds — "Home Fast" is 500/50, NOT the old 100/20 — and they
// DIFFER per technology, so only annotate what's been read off the portal for that technology.
// Unconfirmed ones render without a speed rather than guessing.
const VOCUS_TIER_SPEEDS = {
  FTTP: {
    'Home Fast': '500Mbps/50Mbps',
    'Home Super Fast': '750Mbps/50Mbps',
    'Home Ultra Fast': '1000Mbps/100Mbps',
    'Home Hyper Fast': '2000Mbps/200Mbps',
  },
  // HFC / FTTB / FTTN / FTTC / WIRELESS: named-tier speeds not yet read off the portal.
};

const speedTiersFor = (tech) => Object.keys(VOCUS_PLAN_MATRIX[tech] || {});
const vocusPlanIdFor = (tech, tier) => (VOCUS_PLAN_MATRIX[tech] || {})[tier] || null;

// Label a tier the way the Vocus portal does: "Reseller Connect 100Mbps/40Mbps",
// "Reseller Connect Home Fast (500Mbps/50Mbps)".
const tierLabel = (tech, tier) => {
  const m = /^(\d+)\/(\d+)$/.exec(tier);
  if (m) return `${m[1]}Mbps/${m[2]}Mbps`;
  const speed = VOCUS_TIER_SPEEDS[tech]?.[tier];
  return speed ? `${tier} (${speed})` : `${tier} (speed: check portal)`;
};


const normalizeTech = (raw) => {
  if (!raw) return null;
  const u = String(raw).toUpperCase().trim();
  if (['FTTP','FTTB','FTTN','FTTC','HFC','WIRELESS'].includes(u)) return u;
  if (u === 'FIBRE' || u === 'FIBER') return 'FTTP';
  if (u === 'FW' || u === 'FIXEDWIRELESS') return 'WIRELESS';
  return u;
};

const formatPremiseLabel = (raw, streetNumber) => {
  let s = String(raw || '').replace(/\s+/g, ' ').trim();
  if (!s) return '';
  let level = '';
  const lvl = s.match(/\s*\(([^)]*)\)\s*$/);
  if (lvl && typeof lvl.index === 'number') { level = lvl[1].trim(); s = s.slice(0, lvl.index).trim(); }
  let unit = ''; let street = s;
  const sn = String(streetNumber || '').trim();
  if (sn) { const idx = s.lastIndexOf(' ' + sn + ' '); if (idx > 0) { unit = s.slice(0, idx).trim(); street = s.slice(idx + 1).trim(); } }
  const lead = [unit, level].filter(Boolean).join(', ');
  return lead ? `${lead} · ${street}` : street;
};

// Vocus AVCIDs are `AVC` + 12 digits. Admins paste them lowercase, spaced, or bare —
// normalise before we send one to Vocus (buildFibreQualifyRequest does the same server-side).
const normalizeAvc = (raw) => {
  const v = String(raw || '').trim().toUpperCase().replace(/\s+/g, '');
  if (!v) return '';
  if (/^\d{12}$/.test(v)) return `AVC${v}`;
  return v;
};
const isValidAvc = (raw) => /^AVC\d{9,14}$/.test(normalizeAvc(raw));

// Peaknet's PPPoE auth realm — the vendor portal's "NBN Domain" field.
const NBN_REALM = 'peaknet.com.au';

// Credential defaults. These mirror generatePppoeUsername/generatePppoePassword in
// src/services/vocusBridge.js — the lambda applies the same rule when the UI sends nothing,
// but we generate here so the admin sees (and can copy) the exact values before submitting.

// LOC000192977184 → 000192977184@peaknet.com.au
const pppoeUsernameFromLoc = (loc, realm = NBN_REALM) => {
  const digits = String(loc || '').replace(/\D/g, '');
  return digits ? `${digits}@${realm}` : '';
};

// 8 letters from the customer's name + 3 random digits, e.g. "JohnSmit482".
// Short names are padded from a consonant pool so the length is always 11.
const PW_POOL = 'bcdfghjkmnpqrstvwxz';
const randomInt = (max) => {
  const buf = new Uint32Array(1);
  window.crypto.getRandomValues(buf);
  return buf[0] % max;
};
const pppoePasswordFromName = (customerName) => {
  const letters = String(customerName || '').replace(/[^A-Za-z]/g, '');
  let base = letters.slice(0, 8);
  while (base.length < 8) base += PW_POOL.charAt(randomInt(PW_POOL.length));
  base = base.charAt(0).toUpperCase() + base.slice(1).toLowerCase();
  return `${base}${String(randomInt(1000)).padStart(3, '0')}`;
};
const fmtMbps = (n) => (n == null ? '—' : `${n}Mbps`);

// Vocus FIBRE `Phone` is integer(10) — ten national digits (0433222999). Signup stores E.164,
// so prefills arrive as +61… and Vocus rejects them with FIBRE-4220. Mirrors
// normalizeAuPhoneForFibre in src/services/vocusBridge.js; returns '' when it can't normalise.
// Last-ditch source for the LocationReference postcode: several customers have empty
// postcode/suburb/state but a single-line address that ends in one.
const postcodeFromAddress = (addr) => {
  const m = String(addr || '').trim().match(/\b(\d{4})\s*$/);
  return m ? m[1] : '';
};

const normalizeAuPhone = (raw) => {
  const digits = String(raw == null ? '' : raw).replace(/\D/g, '');
  let n = digits;
  if (n.length === 11 && n.startsWith('61')) n = `0${n.slice(2)}`;
  else if (n.length === 9 && !n.startsWith('0')) n = `0${n}`;
  return /^0\d{9}$/.test(n) ? n : '';
};

// Signup stores the whole address in `address`; the structured street fields are only
// populated for some records (several live customers have address ONLY), so prefer the
// single-line value and assemble from parts as the fallback.
const customerAddressLine = (c) => {
  const whole = String(c?.address || '').replace(/\s+/g, ' ').trim();
  if (whole) return [whole, c?.postcode && !whole.includes(c.postcode) ? c.postcode : ''].filter(Boolean).join(' ');
  return [
    [c?.unitType, c?.unitNumber].filter(Boolean).join(' '),
    [c?.streetNumber, c?.streetName, c?.streetType].filter(Boolean).join(' '),
    c?.suburb, c?.state, c?.postcode,
  ].filter(Boolean).join(' ').replace(/\s+/g, ' ').trim();
};

// ─── Sub-components ──────────────────────────────────────────────────────────

function Field({ label, v, on, placeholder, required, type = 'text', readOnly, mono }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-[#a89984] mb-1">{label}{required && <span className="text-[#fb4934] ml-0.5">*</span>}</span>
      <input
        type={type}
        value={v}
        onChange={readOnly ? undefined : (e) => on(e.target.value)}
        readOnly={readOnly}
        placeholder={placeholder}
        className={`w-full px-3 py-2 rounded-lg bg-[#1d2021] border border-[#3c3836] text-[#ebdbb2] focus:outline-none focus:border-[#fabd2f] ${readOnly ? 'opacity-60 cursor-not-allowed' : ''} ${mono ? 'font-mono text-sm' : ''}`}
      />
    </label>
  );
}

function SelectField({ label, v, on, options, required }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-[#a89984] mb-1">{label}{required && <span className="text-[#fb4934] ml-0.5">*</span>}</span>
      <select value={v} onChange={(e) => on(e.target.value)}
        className="w-full px-3 py-2 rounded-lg bg-[#1d2021] border border-[#3c3836] text-[#ebdbb2] focus:outline-none focus:border-[#fabd2f]">
        {options.map((o) => (
          <option key={typeof o === 'string' ? o : o.value} value={typeof o === 'string' ? o : o.value}>
            {typeof o === 'string' ? o : o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

// One label/value pair on the availability page (mirrors the vendor portal's two-column header).
function DetailRow({ k, v, mono, tone }) {
  const colour = tone === 'warn' ? 'text-[#fb4934]' : tone === 'good' ? 'text-[#b8bb26]' : 'text-[#ebdbb2]';
  return (
    <div className="flex gap-3 py-1">
      <span className="w-52 shrink-0 text-xs font-semibold text-[#a89984] pt-0.5">{k}</span>
      <span className={`text-sm ${colour} ${mono ? 'font-mono' : ''} break-words`}>{v == null || v === '' ? '—' : v}</span>
    </div>
  );
}

// UNI-D port pill. Green = free to order, amber = in use (vendor portal's legend).
function PortPill({ port }) {
  const free = port.available === true;
  return (
    <span
      title={port.epid ? `In use — EPID ${port.epid}` : (free ? 'Available to order' : 'Not available to order')}
      className={`inline-block px-2 py-0.5 rounded text-[11px] font-semibold mr-1 mb-1 ${free ? 'bg-[#b8bb26] text-[#1d2021]' : 'bg-[#d79921]/30 text-[#fabd2f] border border-[#d79921]/60'}`}>
      {port.portName || `UNI-D${port.portNumber ?? '?'}`}
    </span>
  );
}

function SectionHeader({ title, subtitle }) {
  return (
    <div className="mb-4">
      <h2 className="text-base font-semibold text-[#ebdbb2]">{title}</h2>
      {subtitle && <p className="text-xs text-[#928374] mt-0.5">{subtitle}</p>}
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

export default function ProvisionTool() {
  // ── Step: 'start' | 'qualify' | 'availability' | 'order' | 'result' ──
  const [step, setStep] = useState('start');

  // ── Order journey, chosen up-front like the Vocus vendor portal ──
  //   'NEW'      — brand-new AVC on a free UNI-D port. Qualify the address, pick an NTD/port.
  //   'TRANSFER' — take over an existing AVC from another RSP. The AVC must be verified
  //                against the LOC first (SQ with AVCID → AVCIDMatch/AVCIDTarget/CurrentEPID),
  //                exactly as the vendor portal does — an unverified AVC is rejected at Create.
  const [journey, setJourney] = useState(null);
  const [avcInput, setAvcInput] = useState('');
  const [locInput, setLocInput] = useState('');
  // NTD row picked on the availability page → carried into the FIBRE Create.
  const [selectedNtd, setSelectedNtd] = useState(null);

  // ── Signup-customer load (provision an existing pending signup for real) ──
  const [allCustomers, setAllCustomers] = useState([]);
  const [custLoading, setCustLoading] = useState(false);
  const [custQuery, setCustQuery] = useState('');
  const [showCustList, setShowCustList] = useState(false);
  const [showAllCustomers, setShowAllCustomers] = useState(false);
  // When set, submit provisions THIS customer for real (action='provision',
  // persists snapshot + PPPoE creds to the record). null = standalone manual order.
  const [loadedCustomer, setLoadedCustomer] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setCustLoading(true);
      try {
        const token = localStorage.getItem('adminToken');
        const res = await fetch(API_ENDPOINTS.ADMIN_GET_CUSTOMERS, {
          headers: { 'Content-Type': 'application/json', Authorization: token ? `Bearer ${token}` : '' },
        });
        const data = await res.json().catch(() => ({}));
        const list = Array.isArray(data?.customers) ? data.customers : Array.isArray(data) ? data : [];
        if (!cancelled) setAllCustomers(list);
      } catch {
        /* non-fatal — manual entry still works */
      } finally {
        if (!cancelled) setCustLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Plans catalogue — needed to turn a customer's Peaknet plan name into the Vocus
  // speed tier. Without this the form would fall back to a hardcoded default and
  // silently order the wrong speed for a loaded customer.
  const [plans, setPlans] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('adminToken');
        const res = await fetch(API_ENDPOINTS.PLAN_MANAGEMENT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: token ? `Bearer ${token}` : '' },
          body: JSON.stringify({ action: 'getAll' }),
        });
        const data = await res.json().catch(() => ({}));
        if (!cancelled && Array.isArray(data?.plans)) setPlans(data.plans);
      } catch {
        /* non-fatal — the admin picks the tier manually */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // A customer's plan name → the Vocus speed tier on its Plans row. Returns '' when the plan
  // isn't in the catalogue (legacy names like "Premium Home Plan" aren't), which forces the
  // admin to choose rather than letting a default through.
  const speedTierForPlan = (planName) => {
    const want = String(planName || '').trim().toLowerCase();
    if (!want) return '';
    const row = plans.find((pl) => [pl.planName, pl.name].filter(Boolean)
      .some((n) => String(n).trim().toLowerCase() === want));
    return row?.vocusSpeedTier || '';
  };

  // ── Address / Qualify state ──
  const [addrForm, setAddrForm] = useState({
    streetNumber: '', streetName: '', streetType: 'ST',
    suburb: '', state: 'NSW', postcode: '', unitNumber: '', unitType: '',
  });
  const [qualifying, setQualifying] = useState(false);
  const [qualResult, setQualResult] = useState(null);
  const [qualError, setQualError] = useState(null);
  const [searching, setSearching] = useState(false);
  const [premises, setPremises] = useState(null);
  const [searchError, setSearchError] = useState(null);
  const [activeDirectoryId, setActiveDirectoryId] = useState(null);
  // The DIR record behind the qualification. FIBRE::Get QUALIFY returns no address of its
  // own, so without this the Address row on the availability page is blank and there is
  // nothing on screen telling the operator WHICH unit of the building they are about to
  // order for. The LOC and this address come from the same BroadbandAddressRecord.
  const [activePremise, setActivePremise] = useState(null);
  const [addressText, setAddressText] = useState('');

  // ── Order form state ──
  const [order, setOrder] = useState({
    // Customer
    customerName: '', phone: '',
    // Credentials
    username: '', password: '',
    // Order
    orderType: 'NEW', avcid: '', dataPortNumber: 'Auto', ntdId: '',
    // Plan
    speedTier: '100/40', planMapping: 'default', planId: '', customerPlanName: '',
    // Shown while provisioning so the premise can be validated against the customer record.
    accountNumber: '', customerAddress: '',
    // Customer Authority date (YYYYMMDD), mandatory for TRANSFER/OUTSTANDING.
    caDate: '',
    // Service
    serviceType: '', serviceLevel: 'STANDARD',
    ipAssignment: 'CGNAT', authType: 'PPPoE',
    orderReference: '',
    // From QUALIFY (read-only display)
    directoryId: '', csa: '', qualifyPostcode: '',
    addressComponents: null,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  const updateAddr = (k, v) => setAddrForm((f) => ({ ...f, [k]: v }));
  const updateOrder = (k, v) => setOrder((o) => ({ ...o, [k]: v }));

  // Credential defaults, applied once when the order form opens — same rule as the vendor portal:
  // username = LOC digits + NBN domain, password = 8 name letters + 3 digits. Only fills blanks, so a
  // loaded customer's stored credentials (and anything the admin typed) survive. Deliberately keyed on
  // `step` alone so typing a name doesn't regenerate the password mid-keystroke — use Generate for that.
  // CADate is mandatory on transfers; default to today so the common case needs no thought.
  useEffect(() => {
    if (order.orderType === 'NEW' || order.caDate) return;
    const d = new Date();
    updateOrder('caDate', `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`);
  }, [order.orderType, order.caDate]);

  useEffect(() => {
    if (step !== 'order') return;
    setOrder((o) => ({
      ...o,
      // Username is deliberately NOT prefilled — Vocus generates the LOC-derived one itself and
      // appends its own trailing digits (000192977184`11`@…). Filling it here would send a param
      // the Product Catalogue examples never send, and would drop those digits. Override only.
      password: o.password.trim() || (o.customerName.trim() ? pppoePasswordFromName(o.customerName) : ''),
    }));
  }, [step]);

  const buildAddrComponents = (src) => ({
    streetNumber: String((src || addrForm).streetNumber || '').trim().toUpperCase(),
    streetName: String((src || addrForm).streetName || '').trim().toUpperCase(),
    streetType: String((src || addrForm).streetType || '').trim().toUpperCase(),
    suburb: String((src || addrForm).suburb || '').trim().toUpperCase(),
    city: String((src || addrForm).suburb || '').trim().toUpperCase(),
    state: (src || addrForm).state,
    postcode: String((src || addrForm).postcode || '').trim(),
    unitNumber: String((src || addrForm).unitNumber || '').trim().toUpperCase(),
    unitType: String((src || addrForm).unitType || '').trim().toUpperCase(),
  });

  const hasAddress = () => Boolean(
    addrForm.streetName.trim() && addrForm.streetType.trim() && addrForm.suburb.trim()
    && addrForm.state && addrForm.postcode.trim()
  );

  // ── QUALIFY ──
  // On the TRANSFER journey the AVCID goes IN with the SQ, so Vocus verifies it against the
  // LOC and tells us AVCIDMatch / AVCIDTarget (the UNI-D port carrying it) / CurrentEPID (the
  // losing RSP) — the same check the vendor portal makes before it will accept a transfer order.
  const runQualify = async (e, directoryId = null) => {
    e?.preventDefault?.();
    const manualLoc = locInput.trim().toUpperCase();
    const dirId = directoryId || manualLoc || null;
    setQualError(null); setQualResult(null); setSelectedNtd(null); setActiveDirectoryId(dirId);
    // A LOC typed by hand, or an address-only qualify, has no premise record behind it.
    if (!directoryId) setActivePremise(null);

    const avc = normalizeAvc(avcInput);
    if (journey === 'TRANSFER') {
      if (!avc) { setQualError('AVC ID is required on a transfer — Vocus verifies it against the LOC before the order can be placed.'); return; }
      if (!isValidAvc(avc)) { setQualError(`"${avcInput.trim()}" is not a valid AVC ID. Expected AVC + 12 digits, e.g. AVC000245601980.`); return; }
    }
    // A LOC on its own is enough to qualify; without one we need a full address to look it up.
    if (!dirId && !hasAddress()) {
      setQualError('Enter the LOC / DirectoryID, or a full address (street name, street type, suburb, state, postcode).'); return;
    }
    if (!dirId && !/^\d{4}$/.test(addrForm.postcode.trim())) { setQualError('Postcode must be 4 digits.'); return; }
    if (dirId && !/^LOC/i.test(dirId)) { setQualError(`"${dirId}" is not a valid NBN LOC — expected LOC + 12 digits, e.g. LOC000192977184.`); return; }

    setQualifying(true);
    try {
      const addressComponents = hasAddress() ? buildAddrComponents() : null;
      const token = localStorage.getItem('adminToken');
      const res = await fetch(API_ENDPOINTS.VOCUS_AVAILABILITY, {
        method: 'POST',
        // The NTD table / service class / AVC verification are admin-only fields — without the
        // bearer token the lambda returns the sanitised public payload and the page stays empty.
        headers: { 'Content-Type': 'application/json', Authorization: token ? `Bearer ${token}` : '' },
        body: JSON.stringify({
          mode: 'QUALIFY',
          ...(addressComponents ? { addressComponents } : {}),
          ...(dirId ? { directoryId: dirId } : {}),
          ...(journey === 'TRANSFER' && avc ? { avcid: avc } : {}),
        }),
      });
      const data = await res.json().catch(() => ({}));
      setQualResult(data);
      if (!res.ok) { setQualError(data?.message || data?.details || data?.error || `HTTP ${res.status}`); return; }
      if (data.status === 'PASS') {
        const detail = data.qualifyDetail || null;
        const tech = normalizeTech(detail?.serviceType || data.accessTechnology || data.technology);
        const tiersForTech = speedTiersFor(tech);
        const defaultSpeed = tiersForTech.includes('100/40') ? '100/40' : tiersForTech[0] || '';
        const loc = data.nbnLocationId || dirId || order.directoryId;
        if (loc) setLocInput(loc);
        setOrder((o) => ({
          ...o,
          directoryId: loc,
          csa: detail?.csa || data.csa || '',
          // Vocus often returns no postcode; never let that blank one already entered.
          qualifyPostcode: data.qualifyPostcode || addrForm.postcode || o.qualifyPostcode,
          serviceType: tech || '',
          // Keep a loaded customer's plan-derived tier; only default for a manual/standalone order.
          speedTier: loadedCustomer ? o.speedTier : defaultSpeed,
          orderType: journey === 'TRANSFER' ? 'TRANSFER' : 'NEW',
          avcid: journey === 'TRANSFER' ? avc : '',
          // AVCIDTarget is the port the existing service runs on — pre-select it so the
          // transfer takes over the right UNI-D instead of the default port 1.
          dataPortNumber: detail?.avcVerification?.target && /^[1-4]$/.test(String(detail.avcVerification.target))
            ? String(detail.avcVerification.target)
            : o.dataPortNumber,
          addressComponents: addressComponents || o.addressComponents,
        }));
        // The availability page is the vendor-portal view: it needs the admin-only detail.
        // Without it (public payload, or a technology with no NTD table) go straight to the form.
        setStep(detail ? 'availability' : 'order');
      }
    } catch (err) {
      setQualError(err?.message || String(err));
    } finally {
      setQualifying(false);
    }
  };

  // ── Search premises ──
  /**
   * Google Places resolved an address. Fill the Vocus component fields (so the operator
   * can see and correct exactly what we'll send) and pull the premise roster straight
   * away — on a unit block the roster is the only thing that gets the right LOC.
   */
  const onAddressSelected = ({ components, formattedAddress }) => {
    const next = {
      streetNumber: components.streetNumber || '',
      streetName: components.streetName || '',
      streetType: components.streetType || 'ST',
      suburb: components.suburb || '',
      state: AU_STATES.includes(components.state) ? components.state : (components.state || 'NSW'),
      postcode: components.postcode || '',
      unitNumber: components.unitNumber || '',
      unitType: components.unitType || '',
    };
    setAddrForm(next);
    setAddressText(formattedAddress);
    // Never auto-QUALIFY here, even on a single premise: this tool places real orders and
    // the operator should see the premise they're about to commit to before it qualifies.
    void runSearch(null, next);
  };

  const runSearch = async (e, srcForm = null) => {
    e?.preventDefault?.();
    setQualError(null); setQualResult(null); setSearchError(null); setPremises(null); setActiveDirectoryId(null); setActivePremise(null);
    const f = srcForm || addrForm;
    if (!String(f.streetName).trim() || !String(f.streetType).trim() || !String(f.suburb).trim() || !f.state) {
      setSearchError('Street name, street type, suburb and state are required.'); return;
    }
    setSearching(true);
    try {
      const searchComponents = { ...buildAddrComponents(f), unitNumber: '', unitType: '' };
      const res = await fetch(API_ENDPOINTS.VOCUS_AVAILABILITY, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: localStorage.getItem('adminToken') ? `Bearer ${localStorage.getItem('adminToken')}` : '' },
        body: JSON.stringify({ mode: 'SEARCH_NBN_ADDRESS', addressComponents: searchComponents }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setSearchError(data?.message || data?.details || data?.error || `HTTP ${res.status}`); return; }
      const list = Array.isArray(data?.premises) ? data.premises : [];
      setPremises(list);
      if (list.length === 0) setSearchError('No NBN premises found. Try dropping the unit number.');
    } catch (err) {
      setSearchError(err?.message || String(err));
    } finally {
      setSearching(false);
    }
  };

  // ── Manual skip — bypasses the availability page (no QUALIFY, no AVC verification) ──
  const skipToOrder = () => {
    setOrder((o) => ({
      ...o,
      addressComponents: hasAddress() ? buildAddrComponents() : o.addressComponents,
      qualifyPostcode: addrForm.postcode || o.qualifyPostcode,
      directoryId: locInput.trim().toUpperCase() || o.directoryId,
      orderType: journey === 'TRANSFER' ? 'TRANSFER' : o.orderType,
      avcid: journey === 'TRANSFER' ? normalizeAvc(avcInput) : o.avcid,
    }));
    setStep('order');
  };

  // ── Availability page → order form, carrying the NTD/port the admin picked ──
  // mode: 'ntd'  — order on this existing NTD (NTDID = its id)
  //       'new'  — order a NEW NTD at the premise (NTDID = the literal 'NEW'); that's an
  //                install visit, so it must never be reached by accident
  //       'skip' — no NTD chosen; omit NTDID entirely and let Vocus pick the free port
  const chooseNtd = (ntd, port, mode = ntd ? 'ntd' : 'skip') => {
    const pick = {
      ntdId: ntd?.ntdId || null,
      ntdDetail: ntd?.ntdDetail || null,
      portNumber: port?.portNumber || null,
      portName: port?.portName || null,
      isNewNtd: mode === 'new',
      skipped: mode === 'skip',
    };
    setSelectedNtd(pick);
    setOrder((o) => ({
      ...o,
      // 'NEW' is the literal value Vocus expects to order a fresh NTD (FIBRE.pdf §4.1.1).
      // Skipping must send NOTHING — sending 'NEW' would order an install on a premise that
      // already has a usable NTD.
      ntdId: pick.isNewNtd ? 'NEW' : (pick.ntdId || ''),
      // Skipping resets the port to Auto too; picking a row carries that row's port over.
      dataPortNumber: pick.skipped ? 'Auto' : (pick.portNumber ? String(pick.portNumber) : o.dataPortNumber),
    }));
    setStep('order');
  };

  // ── Load a signup customer → prefill the order from their record ──
  // Submitting with a loaded customer provisions FOR REAL against that record.
  const loadCustomer = (c) => {
    if (!c) return;
    const sq = c.vocusSqSnapshot || {};
    const tech = normalizeTech(sq.accessTechnology || sq.technology);
    const orderType = String(c.connectionType || '').toLowerCase() === 'transfer' ? 'TRANSFER' : 'NEW';
    setLoadedCustomer({ UID: c.UID || c.customerId || c.id, email: c.email });
    setAddrForm({
      streetNumber: c.streetNumber || '', streetName: c.streetName || '',
      streetType: c.streetType || 'ST', suburb: c.suburb || '',
      state: c.state || 'NSW', postcode: c.postcode || '',
      unitNumber: c.unitNumber || '', unitType: c.unitType || '',
    });
    setOrder((o) => ({
      ...o,
      customerName: [c.firstName, c.lastName].filter(Boolean).join(' ') || c.email || '',
      // Normalise here so what's shown is what Vocus receives.
      phone: normalizeAuPhone(c.phone) || c.phone || '',
      username: c.vocusPppoeUsername || '',
      password: c.vocusPppoePassword || '',
      orderType,
      avcid: c.avcId || '',
      // Captured at signup for transfers; the admin can still override on the order form.
      dataPortNumber: c.transferDataPort != null && String(c.transferDataPort).trim() !== ''
        ? String(c.transferDataPort)
        : (orderType === 'TRANSFER' ? o.dataPortNumber : 'Auto'),
      // Customer Reference on the Vocus order = the Peaknet account number (PN########), so an
      // order can be traced back to the account from the vendor portal.
      orderReference: c.accountNumber || c.UID || c.customerId || c.email || '',
      accountNumber: c.accountNumber || '',
      customerAddress: customerAddressLine(c),
      // Resolve from the customer's actual plan. If it can't be mapped, leave BLANK so submit
      // blocks — silently defaulting here would order the wrong speed on a real service.
      speedTier: speedTierForPlan(c.plan),
      customerPlanName: c.plan || '',
      serviceType: tech || '',
      directoryId: sq.nbnLocationId || sq.directoryId || '',
      csa: sq.csa || '',
      qualifyPostcode: sq.qualifyPostcode || c.postcode || postcodeFromAddress(c.address) || '',
      addressComponents: {
        unitNumber: c.unitNumber || '', unitType: c.unitType || '',
        streetNumber: c.streetNumber || '', streetName: c.streetName || '',
        streetType: c.streetType || '', suburb: c.suburb || '',
        state: c.state || '', postcode: c.postcode || '',
      },
    }));
    // The signup already told us which journey this is; carry the AVC/LOC it captured into
    // the qualify step so a transfer still gets its AVC verified against the LOC before ordering.
    setJourney(orderType === 'TRANSFER' ? 'TRANSFER' : 'NEW');
    setAvcInput(c.avcId || '');
    setLocInput(sq.nbnLocationId || sq.directoryId || '');
    setSelectedNtd(null);
    setQualResult(null); setQualError(null);
    setShowCustList(false);
    setCustQuery([c.firstName, c.lastName].filter(Boolean).join(' ') || c.email || '');
    setStep('qualify');
  };

  const clearLoadedCustomer = () => {
    setLoadedCustomer(null);
    setCustQuery('');
    // Drop their prefilled identifiers as well — otherwise the next customer picked (or a manual
    // order) silently inherits the previous one's LOC and AVC.
    setLocInput('');
    setAvcInput('');
    setQualResult(null);
    setQualError(null);
    setSelectedNtd(null);
    setOrder((o) => ({ ...o, directoryId: '', csa: '', avcid: '', ntdId: '',
      customerPlanName: '', accountNumber: '', customerAddress: '', orderReference: '' }));
  };

  // A customer still needing an order — that's who this tool is for. Anyone already 'active'
  // is hidden by default so the 15 provisioned records don't bury the 3 that need work.
  const needsProvisioning = (c) => {
    const st = String(c?.provisionStatus || '').trim().toLowerCase();
    return st !== 'active' && st !== 'cancelled';
  };
  const pendingCount = allCustomers.filter(needsProvisioning).length;

  const filteredCustomers = (() => {
    const q = custQuery.trim().toLowerCase();
    const base = showAllCustomers ? allCustomers : allCustomers.filter(needsProvisioning);
    const matched = q
      ? base.filter((c) => [c.email, c.firstName, c.lastName, c.phone, c.plan,
          (c.vocusSqSnapshot || {}).nbnLocationId, `${c.firstName || ''} ${c.lastName || ''}`]
          .filter(Boolean).join(' ').toLowerCase().includes(q))
      : base;
    // Un-provisioned first, then alphabetical — the work queue reads top-down.
    return [...matched].sort((a, b) => {
      const d = (needsProvisioning(b) ? 1 : 0) - (needsProvisioning(a) ? 1 : 0);
      if (d !== 0) return d;
      return String(a.firstName || a.email || '').localeCompare(String(b.firstName || b.email || ''));
    }).slice(0, 60);
  })();

  // ── Submit provision ──
  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setSubmitError(null); setSubmitResult(null);
    if (!order.directoryId && !order.addressComponents) { setSubmitError('LOC / DirectoryID is required — run QUALIFY first or enter manually.'); return; }
    if (!order.customerName.trim()) { setSubmitError('Customer name is required.'); return; }
    if (!normalizeAuPhone(order.phone)) {
      setSubmitError(`Vocus needs a 10-digit Australian phone number (e.g. 0405042322) — "${order.phone.trim() || 'blank'}" won't be accepted. It's sent as FIBRE Phone, which is integer(10).`);
      return;
    }
    if (!order.password.trim()) { setSubmitError('PPPoE password is required.'); return; }
    // FIBRE Create needs a LocationReference, derived from postcode → CSA. Neither is
    // guaranteed: Vocus's AddressLong often carries no postcode, and signup may not have
    // captured one. Catch it here rather than in the SOAP builder after a round trip.
    // LocationReference must be the premise postcode. The CSA is NOT accepted — Vocus returned
    // FIBRE-4220 "Invalid parameters: LocationReference" for CSA400000001009, and the same order
    // with 4504 passed. Vocus's AddressLong frequently omits the postcode, so this often has to
    // be typed by hand.
    if (!/^\d{4}$/.test(order.qualifyPostcode.trim())) {
      setSubmitError('Enter the premise postcode (4 digits) under NBN Location — Vocus uses it as the LocationReference and rejects the order without it. The CSA is not a substitute.');
      return;
    }
    if (!order.speedTier && !order.planId.trim()) {
      setSubmitError(
        order.customerPlanName
          ? `Pick a speed tier — "${order.customerPlanName}" has no vocusSpeedTier in the Plans catalogue, so it can't be mapped to a Vocus PlanID automatically.`
          : 'Pick a speed tier (or enter a PlanID override).'
      );
      return;
    }
    if (order.orderType === 'TRANSFER') {
      if (!isValidAvc(order.avcid)) { setSubmitError('A valid AVC ID (AVC + 12 digits) is required for a TRANSFER order.'); return; }
      // Vocus rejects a Create whose AVCID doesn't belong to the LOC — catch it here rather
      // than burning a real order transaction on it.
      if (avcVerification && avcVerification.match === false) {
        setSubmitError('Vocus did not match this AVC ID to this LOC (AVCIDMatch=FALSE). Re-check the AVC with the customer, or use OUTSTANDING to order without AVC verification.');
        return;
      }
    }
    // 'Auto' = let Vocus pick the free UNI-D port (the vendor portal's default on a NEW order);
    // DataPortNumber is only mandatory on an FTTP/Wireless TRANSFER.
    const dataPortForSubmit = /^[1-4]$/.test(String(order.dataPortNumber))
      ? parseInt(order.dataPortNumber, 10)
      : undefined;
    if (order.orderType === 'TRANSFER' && ['FTTP', 'WIRELESS'].includes(String(tech || '').toUpperCase()) && !dataPortForSubmit) {
      setSubmitError(`A TRANSFER on ${tech} needs the UNI-D port the existing service runs on — pick 1-4, not Auto.`);
      return;
    }
    const token = localStorage.getItem('adminToken');
    setSubmitting(true);
    try {
      // Loaded customer → REAL provision against the record (persists snapshot + creds).
      // The lambda reads LOC/plan/address from the customer; we send only overrides.
      const payload = loadedCustomer
        ? {
            action: 'provision',
            email: loadedCustomer.email,
            customerId: loadedCustomer.UID,
            customerEmail: loadedCustomer.email,
            planId: order.planId.trim() || undefined,
            vocusSpeedTier: order.speedTier || undefined,
            planMapping: order.planMapping || undefined,
            username: order.username.trim() || undefined,
            password: order.password.trim() || undefined,
            orderType: order.orderType,
            avcid: order.avcid.trim() || undefined,
            dataPortNumber: dataPortForSubmit,
            ntdId: order.ntdId || undefined,
            serviceType: order.serviceType || undefined,
            phone: normalizeAuPhone(order.phone) || undefined,
            // These four were only ever sent on the standalone path — on a real customer order
            // the form's choices were silently ignored, which is why picking IPoE still
            // provisioned PPPoE (Vocus defaults when AuthType is absent).
            ipAssignment: order.ipAssignment || undefined,
            authType: order.authType || undefined,
            serviceLevel: order.serviceLevel || undefined,
            orderReference: order.orderReference.trim() || undefined,
            caDate: order.orderType !== 'NEW' ? (order.caDate || undefined) : undefined,
            // From the QUALIFY just run — the customer's stored snapshot is often older and
            // missing these, and without one of them Vocus has no LocationReference.
            csa: order.csa || undefined,
            qualifyPostcode: order.qualifyPostcode || undefined,
            locationReference: order.qualifyPostcode || undefined,
            addressComponents: order.addressComponents || undefined,
            adminUser: 'admin-portal',
          }
        : {
          action: 'standalone_provision',
          directoryId: order.directoryId,
          customerName: order.customerName.trim(),
          phone: normalizeAuPhone(order.phone),
          username: order.username.trim() || undefined,
          password: order.password.trim(),
          orderType: order.orderType,
          avcid: order.avcid.trim() || undefined,
          dataPortNumber: dataPortForSubmit,
          ntdId: order.ntdId || undefined,
          vocusSpeedTier: order.speedTier,
          planMapping: order.planMapping,
          planId: order.planId.trim() || undefined,
          serviceType: order.serviceType,
          serviceLevel: order.serviceLevel,
          ipAssignment: order.ipAssignment,
          authType: order.authType,
          orderReference: order.orderReference.trim() || undefined,
          caDate: order.orderType !== 'NEW' ? (order.caDate || undefined) : undefined,
          csa: order.csa,
          qualifyPostcode: order.qualifyPostcode,
          addressComponents: order.addressComponents,
          adminUser: 'admin-portal',
        };
      const res = await fetch(API_ENDPOINTS.ADMIN_CUSTOMER_MANAGEMENT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token ? `Bearer ${token}` : '' },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.error) {
        setSubmitError(data?.error || data?.details || data?.message || `HTTP ${res.status}`);
      } else {
        setSubmitResult(data);
        setStep('result');
      }
    } catch (err) {
      setSubmitError(err?.message || String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setStep('start');
    setJourney(null); setAvcInput(''); setLocInput(''); setSelectedNtd(null);
    setAddrForm({ streetNumber: '', streetName: '', streetType: 'ST', suburb: '', state: 'NSW', postcode: '', unitNumber: '', unitType: '' });
    setQualResult(null); setQualError(null); setPremises(null); setSearchError(null); setActiveDirectoryId(null); setActivePremise(null); setAddressText('');
    setOrder({ customerName: '', phone: '', username: '', password: '', accountNumber: '', customerAddress: '', orderType: 'NEW', avcid: '', dataPortNumber: 'Auto', ntdId: '', speedTier: '100/40', planMapping: 'default', planId: '', serviceType: '', serviceLevel: 'STANDARD', ipAssignment: 'CGNAT', authType: 'PPPoE', orderReference: '', directoryId: '', csa: '', qualifyPostcode: '', addressComponents: null });
    setSubmitResult(null); setSubmitError(null);
    setLoadedCustomer(null); setCustQuery(''); setShowCustList(false);
  };

  // Start a journey from the landing step.
  const beginJourney = (kind) => {
    setJourney(kind);
    setQualResult(null); setQualError(null); setSelectedNtd(null);
    setOrder((o) => ({ ...o, orderType: kind === 'TRANSFER' ? 'TRANSFER' : 'NEW' }));
    setStep('qualify');
  };

  // Admin-only QUALIFY detail — the vendor-portal availability view.
  const detail = qualResult?.qualifyDetail || null;
  const avcVerification = detail?.avcVerification || null;
  const detailTech = normalizeTech(detail?.serviceType || qualResult?.accessTechnology || qualResult?.technology);

  // The full NTDRecord behind the selection, for the "Current NTD" summary on the order form.
  const selectedNtdRecord = (selectedNtd && !selectedNtd.isNewNtd && detail)
    ? detail.ntds.find((n) => n.ntdId === selectedNtd.ntdId) || null
    : null;

  const tech = normalizeTech(order.serviceType);
  const speedTiers = tech
    ? speedTiersFor(tech)
    : [...new Set(Object.keys(VOCUS_PLAN_MATRIX).flatMap((t) => speedTiersFor(t)))];
  // What the order will actually resolve to — shown so it can be checked against the portal.
  const resolvedPlanId = order.planId.trim() || vocusPlanIdFor(tech, order.speedTier);

  // Identity strip — account number + full signup address, and the premise Vocus actually
  // qualified. Provisioning the wrong premise is the expensive mistake here, so both are shown
  // side by side wherever an order is being built.
  const renderCustomerIdentity = () => {
    if (!loadedCustomer && !order.customerAddress) return null;
    const premise = qualResult?.addressLong || null;
    // Compare loosely — signup rarely stores the postcode inside `address`, and Vocus
    // formats differently ("CCT" vs "CIRCUIT"), so only the street number + name are reliable.
    const key = (t) => String(t || '').toUpperCase().replace(/[^A-Z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();
    const words = (t) => new Set(key(t).split(' ').filter((w) => w.length > 2));
    const overlap = (() => {
      if (!premise || !order.customerAddress) return null;
      const a = words(order.customerAddress);
      const b = words(premise);
      if (!a.size) return null;
      let hit = 0;
      a.forEach((w) => { if (b.has(w)) hit++; });
      return hit / a.size;
    })();
    const streetNo = (t) => (key(t).match(/^(\d+)/) || [])[1] || null;
    const numbersAgree = premise && order.customerAddress
      ? streetNo(order.customerAddress) === streetNo(premise)
      : null;
    const looksWrong = overlap != null && (overlap < 0.5 || numbersAgree === false);

    return (
      <div className={`p-3 rounded-lg border text-xs mb-4 ${
        looksWrong ? 'bg-[#cc241d]/10 border-[#cc241d]/50' : 'bg-[#1d2021] border-[#3c3836]'}`}>
        <div className="flex items-center gap-2 flex-wrap mb-2">
          <FaUser className="text-[#83a598]" />
          <span className="text-[#ebdbb2] font-semibold">{order.customerName || loadedCustomer?.email || '—'}</span>
          {order.accountNumber && (
            <span className="px-2 py-0.5 rounded border border-[#fabd2f]/50 bg-[#fabd2f]/10 text-[#fabd2f] font-mono text-[11px]">
              {order.accountNumber}
            </span>
          )}
          {loadedCustomer?.email && <span className="text-[#a89984]">{loadedCustomer.email}</span>}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <div className="text-[11px] text-[#a89984] mb-0.5">Address on the account (signup)</div>
            <div className="text-[#ebdbb2]">{order.customerAddress || <span className="text-[#665c54]">none recorded</span>}</div>
          </div>
          <div>
            <div className="text-[11px] text-[#a89984] mb-0.5">Premise qualified at Vocus</div>
            <div className={premise ? 'text-[#ebdbb2]' : 'text-[#665c54]'}>
              {premise || 'not qualified yet'}
            </div>
            {order.directoryId && <div className="text-[11px] font-mono text-[#83a598] mt-0.5">{order.directoryId}</div>}
          </div>
        </div>
        {looksWrong && (
          <p className="text-[#fb4934] mt-2">
            ⚠ These don&apos;t look like the same premise — check the LOC before ordering. Provisioning the
            wrong address is not something Vocus will unwind for you.
          </p>
        )}
      </div>
    );
  };

  // Reusable customer dropdown. Opens on click, defaults to the customers still needing an
  // order, and falls back to the full list on demand. Used on both the start and qualify steps.
  const renderCustomerPicker = () => (
    <div className="relative">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <FaUser className="absolute left-3 top-3 text-[#665c54]" />
          <input
            type="text"
            value={custQuery}
            onChange={(e) => { setCustQuery(e.target.value); setShowCustList(true); if (loadedCustomer) setLoadedCustomer(null); }}
            onFocus={() => setShowCustList(true)}
            onClick={() => setShowCustList(true)}
            placeholder={custLoading
              ? 'Loading customers…'
              : `${pendingCount} customer${pendingCount === 1 ? '' : 's'} awaiting provisioning — click to pick, or type to search`}
            className="w-full pl-9 pr-9 py-2 rounded-lg bg-[#1d2021] border border-[#3c3836] text-[#ebdbb2] placeholder-[#665c54] focus:outline-none focus:border-[#fabd2f]"
          />
          <button type="button" onClick={() => setShowCustList((v) => !v)}
            className="absolute right-2 top-2.5 text-[#665c54] hover:text-[#ebdbb2]" title="Show list">
            <FaChevronDown className={`transition ${showCustList ? 'rotate-180' : ''}`} />
          </button>
        </div>
        {(custQuery || loadedCustomer) && (
          <button type="button" onClick={clearLoadedCustomer}
            className="p-2.5 rounded-lg border border-[#504945] bg-[#3c3836] hover:bg-[#504945] text-[#a89984]" title="Clear">
            <FaTimes />
          </button>
        )}
      </div>
      {showCustList && (
        <div className="absolute z-20 mt-1 w-full max-h-80 overflow-y-auto bg-[#1d2021] border border-[#3c3836] rounded-lg shadow-xl">
          <div className="flex items-center justify-between px-3 py-1.5 border-b border-[#3c3836] sticky top-0 bg-[#1d2021]">
            <span className="text-[11px] text-[#a89984]">
              {showAllCustomers ? `All customers (${filteredCustomers.length})` : `Awaiting provisioning (${filteredCustomers.length})`}
            </span>
            <button type="button" onClick={() => setShowAllCustomers((v) => !v)}
              className="text-[11px] text-[#83a598] hover:text-[#8ec07c]">
              {showAllCustomers ? 'Show only un-provisioned' : 'Show all customers'}
            </button>
          </div>
          {filteredCustomers.length === 0 ? (
            <div className="px-4 py-3 text-xs text-[#a89984]">
              {showAllCustomers ? 'No customers match.' : 'Everyone is provisioned — switch to "Show all customers".'}
            </div>
          ) : filteredCustomers.map((c) => {
            const sq = c.vocusSqSnapshot || {};
            const loc = sq.nbnLocationId || sq.directoryId;
            const isTransfer = String(c.connectionType || '').toLowerCase() === 'transfer';
            const done = !needsProvisioning(c);
            return (
              <button key={c.UID || c.customerId || c.email} type="button" onClick={() => loadCustomer(c)}
                className="w-full text-left px-4 py-2.5 hover:bg-[#3c3836] flex items-start gap-3 border-b border-[#3c3836] last:border-0">
                <FaUser className={`mt-1 shrink-0 ${done ? 'text-[#665c54]' : 'text-[#83a598]'}`} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-[#ebdbb2] truncate flex items-center gap-2">
                    {[c.firstName, c.lastName].filter(Boolean).join(' ') || c.email}
                    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${
                      done ? 'border-[#665c54]/60 text-[#a89984]'
                           : 'border-[#d79921]/50 bg-[#d79921]/15 text-[#fabd2f]'}`}>
                      {c.provisionStatus || 'not ordered'}
                    </span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${
                      isTransfer ? 'border-[#458588]/50 text-[#83a598]' : 'border-[#98971a]/50 text-[#b8bb26]'}`}>
                      {isTransfer ? 'TRANSFER' : 'NEW'}
                    </span>
                  </div>
                  <div className="text-[11px] text-[#a89984] truncate">
                    {c.accountNumber ? `${c.accountNumber} · ` : ''}{c.email}{c.plan ? ` · ${c.plan}` : ''}
                  </div>
                  <div className="text-[11px] text-[#d5c4a1] truncate">
                    {customerAddressLine(c) || <span className="text-[#665c54]">no address on record</span>}
                  </div>
                  <div className="text-[11px] font-mono text-[#665c54] truncate">
                    {loc || 'no LOC captured at signup'}
                    {isTransfer ? (c.avcId ? ` · ${c.avcId}` : ' · no AVC captured') : ''}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  // ── Render ──
  return (
    <div className={`p-6 mx-auto ${step === 'availability' ? 'max-w-6xl' : 'max-w-4xl'}`}>
      <div className="flex items-center gap-3 mb-1">
        <FaServer className="text-2xl text-[#fabd2f]" />
        <h1 className="text-2xl font-bold text-[#ebdbb2]">Provision Service</h1>
      </div>
      <p className="text-sm text-[#a89984] mb-6">
        Vocus FIBRE provisioning — pick the order type, qualify the premise, choose the NTD/UNI-D port, then submit a <code className="text-[#fabd2f]">FIBRE::Create</code>.
      </p>

      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-xs text-[#a89984] mb-6 flex-wrap">
        {[
          ['start', '1. Order Type'],
          ['qualify', journey === 'TRANSFER' ? '2. Validate AVC + LOC' : '2. Qualify Address'],
          ['availability', '3. Availability'],
          ['order', '4. Order Details'],
          ['result', '5. Result'],
        ].map(([key, label], i) => (
          <span key={key} className="flex items-center gap-2">
            {i > 0 && <FaChevronRight className="text-[#504945]" />}
            <span className={`px-2 py-0.5 rounded ${step === key ? 'bg-[#fabd2f] text-[#1d2021] font-semibold' : 'text-[#665c54]'}`}>
              {label}
            </span>
          </span>
        ))}
        {journey && (
          <span className={`ml-2 px-2 py-0.5 rounded text-[11px] font-semibold ${journey === 'TRANSFER' ? 'bg-[#83a598]/20 text-[#83a598] border border-[#83a598]/50' : 'bg-[#b8bb26]/20 text-[#b8bb26] border border-[#b8bb26]/50'}`}>
            {journey === 'TRANSFER' ? 'TRANSFER' : 'NEW CONNECTION'}
          </span>
        )}
      </div>

      {/* ── Step 1: Order type — the two vendor-portal journeys ── */}
      {step === 'start' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <button type="button" onClick={() => beginJourney('NEW')}
              className="text-left p-5 rounded-xl border-2 border-[#3c3836] bg-[#282828] hover:border-[#b8bb26] hover:bg-[#32302f] transition group">
              <div className="flex items-center gap-3 mb-2">
                <FaPlusCircle className="text-2xl text-[#b8bb26]" />
                <h2 className="text-lg font-semibold text-[#ebdbb2]">New connection</h2>
              </div>
              <p className="text-sm text-[#a89984]">
                No existing NBN service at the premise (or the customer is adding a second one). Qualify the
                address, then take a free UNI-D port on an existing NTD — or order a new NTD.
              </p>
              <p className="text-[11px] text-[#928374] mt-3">Vocus <code className="text-[#fabd2f]">OrderType=NEW</code> · no AVC ID needed</p>
            </button>

            <button type="button" onClick={() => beginJourney('TRANSFER')}
              className="text-left p-5 rounded-xl border-2 border-[#3c3836] bg-[#282828] hover:border-[#83a598] hover:bg-[#32302f] transition group">
              <div className="flex items-center gap-3 mb-2">
                <FaExchangeAlt className="text-2xl text-[#83a598]" />
                <h2 className="text-lg font-semibold text-[#ebdbb2]">Transfer</h2>
              </div>
              <p className="text-sm text-[#a89984]">
                Take over a service running with another RSP. The customer's <strong>AVC ID</strong> is validated
                against the <strong>LOC</strong> before the order — Vocus confirms the match, the port it runs on,
                and the losing provider.
              </p>
              <p className="text-[11px] text-[#928374] mt-3">Vocus <code className="text-[#fabd2f]">OrderType=TRANSFER</code> · AVC ID required</p>
            </button>
          </div>

          {/* Provision an existing signup customer (real provision against the record) */}
          <div className="bg-[#282828] border border-[#3c3836] rounded-xl p-5 mb-4">
            <SectionHeader
              title="Provision a signup customer"
              subtitle="Pick a customer to prefill the whole order from their signup — LOC, technology, plan, address, contact and credentials — and provision FOR REAL against their record. Or scroll down for a one-off manual address."
            />
            {renderCustomerPicker()}
          </div>
        </>
      )}

      {/* ── Step 2: Qualify (TRANSFER also validates the AVC against the LOC) ── */}
      {step === 'qualify' && (
        <>
          <div className="flex items-center gap-3 mb-4">
            <button type="button" onClick={() => setStep('start')}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-[#504945] bg-[#3c3836] hover:bg-[#504945] text-[#a89984] text-xs">
              <FaArrowLeft /> Order type
            </button>
            {loadedCustomer && (
              <span className="text-xs text-[#fabd2f] flex items-center gap-2 flex-wrap">
                Provisioning <strong>{loadedCustomer.email}</strong> for real — prefilled from their signup.
                <button type="button" onClick={clearLoadedCustomer}
                  className="px-2 py-0.5 rounded border border-[#504945] bg-[#3c3836] hover:bg-[#504945] text-[#a89984] text-[11px]">
                  Pick a different customer
                </button>
              </span>
            )}
          </div>

          {renderCustomerIdentity()}


          {/* Load a customer here too — the LOC/AVC below then fill themselves in from signup. */}
          {!loadedCustomer && (
            <div className="bg-[#282828] border border-[#3c3836] rounded-xl p-5 mb-4">
              <SectionHeader
                title="Provisioning a signup customer?"
                subtitle="Pick them here and the LOC, AVC, technology, plan, address, contact and credentials all fill in from their signup."
              />
              {renderCustomerPicker()}
            </div>
          )}

          {/* LOC + (transfer) AVC — the vendor portal validates both before it will show ordering */}
          <div className="bg-[#282828] border border-[#3c3836] rounded-xl p-5 space-y-4 mb-4">
            <SectionHeader
              title={journey === 'TRANSFER' ? 'Service identifiers' : 'Known LOC (optional)'}
              subtitle={journey === 'TRANSFER'
                ? "The AVC ID is sent WITH the qualification so Vocus verifies it belongs to this LOC and tells us which UNI-D port it runs on and who the current provider is. A transfer can't be ordered without this check."
                : 'If you already have the NBN LOC, qualify it directly and skip the address lookup below.'}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="LOC / DirectoryID" v={locInput} on={setLocInput} placeholder="LOC000192977184" mono
                required={journey === 'TRANSFER' && !hasAddress()} />
              {journey === 'TRANSFER' && (
                <Field label="AVC ID (existing service)" v={avcInput} on={setAvcInput} placeholder="AVC000245601980" mono required />
              )}
            </div>
            {journey === 'TRANSFER' && avcInput.trim() && !isValidAvc(avcInput) && (
              <p className="text-xs text-[#fb4934]">Expected AVC + 12 digits (e.g. AVC000245601980).</p>
            )}
            <button type="button" onClick={() => runQualify(null, null)}
              disabled={qualifying || searching || (!locInput.trim() && !hasAddress())}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#fabd2f] hover:bg-[#fe8019] text-[#1d2021] font-semibold disabled:opacity-50">
              <FaShieldAlt />{qualifying ? 'Qualifying…' : journey === 'TRANSFER' ? 'Validate AVC + LOC' : 'QUALIFY'}
            </button>
            {qualError && (
              <div className="p-3 rounded-lg bg-red-900/40 border border-red-700 text-red-200 text-sm flex items-start gap-2">
                <FaExclamationTriangle className="mt-0.5 shrink-0" /> {qualError}
              </div>
            )}
            {qualResult && !qualifying && qualResult.status && qualResult.status !== 'PASS' && (
              <div className="p-3 rounded-lg bg-amber-900/30 border border-amber-700/60 text-amber-200 text-sm flex items-start gap-2">
                <FaExclamationTriangle className="mt-0.5 shrink-0" /> {qualResult.status} — {qualResult.message || 'not serviceable at this LOC.'}
              </div>
            )}
          </div>

          <form onSubmit={runQualify} className="bg-[#282828] border border-[#3c3836] rounded-xl p-5 space-y-4 mb-4">
            <SectionHeader
              title="Address Lookup"
              subtitle="Search the address, then pick the exact premise. On a multi-unit building the roster is the only thing that resolves the right LOC — qualifying the street address collapses every unit to one record on the Vocus side."
            />
            <AddressAutocomplete
              value={addressText}
              onChange={setAddressText}
              onSelect={onAddressSelected}
              disabled={qualifying || searching}
              placeholder="Start typing the customer's address…"
            />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="Unit (optional)" v={addrForm.unitNumber} on={(v) => updateAddr('unitNumber', v)} />
              <Field label="Street number" v={addrForm.streetNumber} on={(v) => updateAddr('streetNumber', v)} placeholder="43" />
              <Field label="Street name" v={addrForm.streetName} on={(v) => updateAddr('streetName', v)} placeholder="OVERALL" required />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <SelectField label="Street type" v={addrForm.streetType} on={(v) => updateAddr('streetType', v)} options={STREET_TYPES} required />
              <Field label="Suburb" v={addrForm.suburb} on={(v) => updateAddr('suburb', v)} placeholder="CASEY" required />
              <SelectField label="State" v={addrForm.state} on={(v) => updateAddr('state', v)} options={AU_STATES} required />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="Postcode" v={addrForm.postcode} on={(v) => updateAddr('postcode', v.replace(/\D/g,'').slice(0,4))} placeholder="2913" required />
            </div>
            <div className="flex items-center gap-3 pt-2 flex-wrap">
              <button type="submit" disabled={qualifying || searching}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#fabd2f] hover:bg-[#fe8019] text-[#1d2021] font-semibold disabled:opacity-50">
                <FaWifi />{qualifying ? 'Qualifying…' : 'QUALIFY Address'}
              </button>
              <button type="button" onClick={runSearch} disabled={qualifying || searching}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#83a598] hover:bg-[#8ec07c] text-[#1d2021] font-semibold disabled:opacity-50">
                <FaList />{searching ? 'Searching…' : 'Find all premises'}
              </button>
              <button type="button" onClick={skipToOrder}
                className="px-4 py-2.5 rounded-lg border border-[#504945] bg-[#3c3836] hover:bg-[#504945] text-[#a89984] text-sm">
                Skip (manual entry)
              </button>
              <button type="button" onClick={() => { setAddrForm({ streetNumber:'',streetName:'',streetType:'ST',suburb:'',state:'NSW',postcode:'',unitNumber:'',unitType:'' }); setAddressText(''); setQualResult(null); setQualError(null); setPremises(null); setSearchError(null); setActivePremise(null); }}
                className="px-4 py-2.5 rounded-lg border border-[#504945] bg-[#3c3836] hover:bg-[#504945] text-[#ebdbb2] text-sm">
                Reset
              </button>
            </div>
          </form>

          {qualError && (
            <div className="p-4 rounded-lg bg-red-900/40 border border-red-700 text-red-200 text-sm flex items-start gap-2 mb-4">
              <FaExclamationTriangle className="mt-0.5 shrink-0" /> {qualError}
            </div>
          )}
          {searchError && (
            <div className="p-4 rounded-lg bg-amber-900/40 border border-amber-700 text-amber-200 text-sm flex items-start gap-2 mb-4">
              <FaExclamationTriangle className="mt-0.5 shrink-0" /> {searchError}
            </div>
          )}

          {/* Premise list */}
          {Array.isArray(premises) && premises.length > 0 && (
            <div className="bg-[#282828] border border-[#3c3836] rounded-xl overflow-hidden mb-4">
              <div className="px-4 py-2 flex items-center justify-between bg-[#1d2021] border-b border-[#3c3836]">
                <h2 className="text-xs font-semibold uppercase tracking-wide text-[#a89984]">Premises found ({premises.length})</h2>
                <span className="text-[11px] text-[#a89984]">Click to QUALIFY that LOC</span>
              </div>
              <div className="max-h-72 overflow-y-auto divide-y divide-[#3c3836]">
                {premises.map((p) => {
                  const active = activeDirectoryId === p.directoryId;
                  return (
                    <button key={p.directoryId} type="button"
                      onClick={() => { setActiveDirectoryId(p.directoryId); setActivePremise(p); runQualify(null, p.directoryId); }}
                      disabled={qualifying}
                      className={`w-full text-left px-4 py-3 flex items-start gap-3 transition disabled:opacity-50 ${active ? 'bg-[#fabd2f]/15' : 'hover:bg-[#3c3836]'}`}>
                      <FaMapMarkerAlt className={`mt-0.5 shrink-0 ${active ? 'text-[#fabd2f]' : 'text-[#83a598]'}`} />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-[#ebdbb2] truncate">
                          {p.addressLong ? formatPremiseLabel(p.addressLong, addrForm.streetNumber) : '(no address text)'}
                        </div>
                        <div className="text-[11px] text-[#a89984] font-mono mt-0.5">
                          {p.directoryId}{p.carrier ? ` · ${p.carrier}` : ''}{p.locationReference ? ` · ${p.locationReference}` : ''}
                        </div>
                      </div>
                      {qualifying && active
                        ? <FaSearch className="mt-0.5 shrink-0 text-[#fabd2f] animate-pulse" />
                        : <span className="text-[11px] text-[#a89984] mt-0.5 shrink-0">Qualify →</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* QUALIFY result (pass/fail) */}
          {qualResult && !qualifying && (
            <div className={`p-4 rounded-xl border mb-4 ${qualResult.status === 'PASS' ? 'bg-emerald-900/30 border-emerald-700/60' : 'bg-amber-900/30 border-amber-700/60'}`}>
              <div className="flex items-center gap-2 mb-2">
                {qualResult.status === 'PASS'
                  ? <FaCheckCircle className="text-emerald-400 text-xl" />
                  : <FaExclamationTriangle className="text-amber-400 text-xl" />}
                <h3 className={`text-lg font-semibold ${qualResult.status === 'PASS' ? 'text-emerald-200' : 'text-amber-200'}`}>
                  {qualResult.status === 'PASS' ? 'PASS — proceed to order details' : `${qualResult.status} — not serviceable`}
                </h3>
              </div>
              {qualResult.status === 'PASS' && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                  {[['Technology', qualResult.accessTechnology || qualResult.technology], ['LOC', qualResult.nbnLocationId], ['CSA', qualResult.csa], ['Speeds', Array.isArray(qualResult.availablePlans) ? qualResult.availablePlans.join('/') + ' Mbps' : '—']].map(([k, v]) => (
                    <div key={k}><div className="text-xs text-[#a89984]">{k}</div><div className="text-sm font-semibold text-[#ebdbb2] font-mono">{v ?? '—'}</div></div>
                  ))}
                </div>
              )}
              {qualResult.status === 'PASS' && !qualResult.qualifyDetail && (
                <p className="text-xs text-amber-200/80 mt-3">
                  Vocus returned a PASS but no NTD/port detail — the admin token wasn't accepted, so this is the
                  public payload. Re-login to the admin portal to see the full availability page.
                </p>
              )}
            </div>
          )}
        </>
      )}

      {/* ── Step 3: Availability — the vendor-portal NBN Availability page ── */}
      {step === 'availability' && detail && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => setStep('qualify')}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-[#504945] bg-[#3c3836] hover:bg-[#504945] text-[#a89984] text-xs">
              <FaArrowLeft /> Qualify again
            </button>
            {loadedCustomer && (
              <span className="text-xs text-[#fabd2f]">Provisioning <strong>{loadedCustomer.email}</strong> for real.</span>
            )}
          </div>

          {renderCustomerIdentity()}


          {/* Result header */}
          <div className="bg-[#282828] border border-[#3c3836] rounded-xl overflow-hidden">
            <div className="px-5 py-3 bg-[#1d2021] border-b border-[#3c3836] flex items-center justify-between flex-wrap gap-2">
              <h2 className="text-base font-semibold text-[#ebdbb2]">
                {(detailTech || 'NBN')} NBN {journey === 'TRANSFER' ? 'Transfer' : 'New'} Order
              </h2>
              <span className={`px-2 py-0.5 rounded text-xs font-semibold ${detail.result === 'PASS' ? 'bg-[#b8bb26] text-[#1d2021]' : 'bg-[#fb4934] text-[#1d2021]'}`}>
                {detail.result || qualResult?.status || '—'}
              </span>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-10 p-5">
              <div>
                <DetailRow k="Result" v={detail.result} tone={detail.result === 'PASS' ? 'good' : 'warn'} />
                {/* Vocus returns the family ("FIBRE"); show the NBN technology code the rest of the tool uses. */}
                <DetailRow k="Service Type" v={detailTech || detail.serviceType || qualResult?.accessTechnology} />
                <DetailRow k="Availability" v={detail.result === 'PASS' ? `NBN ${detailTech || detail.serviceType || ''} is available.`.replace('  ', ' ') : (qualResult?.message || '—')} />
                <DetailRow k="Service Class" v={detail.serviceClass} mono />
                <DetailRow k="CSA ID" v={detail.csa} mono />
                <DetailRow k="Zone" v={detail.zone} />
                {detail.notes.length > 0 && (
                  <DetailRow k="Additional Information" v={detail.notes.join(' ')} tone="warn" />
                )}
                {detail.features.length > 0 && (
                  <DetailRow k="Higher Speed Tier" v={detail.features.map((f) => `${f.name}: ${f.value}`).join(' · ')} />
                )}
              </div>
              <div>
                <DetailRow k="Connection Type" v={detail.connectionType} />
                <DetailRow k="Location ID" v={activePremise?.directoryId || order.directoryId || qualResult?.nbnLocationId} mono />
                {/* QUALIFY echoes no address back, so this is the picked premise's own
                    AddressLong — same DIR record as the LOC above, so the two cannot
                    disagree. Read it before ordering: it is the only on-screen confirmation
                    of which unit in the building this order is for. */}
                <DetailRow k="Address" v={activePremise?.addressLong || qualResult?.addressLong} />
                {!activePremise && !qualResult?.addressLong && (
                  <p className="ml-[13.75rem] -mt-0.5 mb-1 text-[11px] text-[#d79921]">
                    Qualified by LOC or street address, so the exact premise is unconfirmed — Vocus returns no
                    address on this path. Use &ldquo;Find all premises&rdquo; and pick one before ordering.
                  </p>
                )}
                <DetailRow k="New Development Charges"
                  v={detail.developmentCharge == null ? '—' : detail.developmentCharge ? 'Applicable' : 'Not applicable'}
                  tone={detail.developmentCharge ? 'warn' : undefined} />
                <DetailRow k="CVC ID" v={detail.cvcId} mono />
                {detail.activationDate && <DetailRow k="NBN Activation Date" v={detail.activationDate} mono />}
                {detail.copperDisconnectionDate && <DetailRow k="Copper Disconnection" v={detail.copperDisconnectionDate} mono />}
                {detail.hfcSelfInstall != null && <DetailRow k="HFC Self Install" v={detail.hfcSelfInstall ? 'Eligible' : 'Not eligible'} />}
              </div>
            </div>
          </div>

          {/* AVC verification — transfer only */}
          {journey === 'TRANSFER' && (
            <div className={`p-4 rounded-xl border ${
              avcVerification?.match === true ? 'bg-emerald-900/30 border-emerald-700/60'
                : avcVerification?.match === false ? 'bg-red-900/40 border-red-700'
                : 'bg-amber-900/30 border-amber-700/60'}`}>
              <div className="flex items-center gap-2 mb-2">
                {avcVerification?.match === true ? <FaCheckCircle className="text-emerald-400" />
                  : avcVerification?.match === false ? <FaTimesCircle className="text-red-400" />
                  : <FaExclamationTriangle className="text-amber-400" />}
                <h3 className="text-sm font-semibold text-[#ebdbb2]">
                  {avcVerification?.match === true ? 'AVC verified against this LOC'
                    : avcVerification?.match === false ? 'AVC does NOT match this LOC'
                    : 'Vocus returned no AVC verification for this qualification'}
                </h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                <div><div className="text-xs text-[#a89984]">AVC ID</div><div className="font-mono text-[#ebdbb2]">{normalizeAvc(avcInput) || '—'}</div></div>
                <div><div className="text-xs text-[#a89984]">Target port / copper pair</div><div className="font-mono text-[#ebdbb2]">{avcVerification?.target || '—'}</div></div>
                <div><div className="text-xs text-[#a89984]">Current provider (EPID)</div><div className="font-mono text-[#ebdbb2]">{avcVerification?.currentEpid || '—'}</div></div>
              </div>
              {avcVerification?.match === false && (
                <p className="text-xs text-red-200 mt-3">
                  Vocus will reject a TRANSFER Create with this AVC. Re-check the AVC ID with the customer, confirm
                  you qualified the right LOC, or order as <code>OUTSTANDING</code> (no AVC verification).
                </p>
              )}
              {avcVerification == null && (
                <p className="text-xs text-amber-200 mt-3">
                  No AVCIDMatch/AVCIDTarget came back. That happens when the SQ ran without an AVC ID, or the
                  technology doesn't support AVC verification. Ordering a transfer on an unverified AVC is risky.
                </p>
              )}
            </div>
          )}

          {/* NTD / UNI-D port table */}
          <div className="bg-[#282828] border border-[#3c3836] rounded-xl overflow-hidden">
            <div className="px-5 py-3 bg-[#1d2021] border-b border-[#3c3836] flex items-center justify-between flex-wrap gap-2">
              <h2 className="text-sm font-semibold text-[#ebdbb2] flex items-center gap-2"><FaNetworkWired className="text-[#83a598]" /> Network termination devices</h2>
              <span className="text-[11px] text-[#a89984] flex items-center gap-3">
                Data Port Status:
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#b8bb26] inline-block" /> Available to order</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#d79921] inline-block" /> Not available to order</span>
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-[#a89984] border-b border-[#3c3836]">
                    <th className="px-4 py-2 font-semibold">NTD ID</th>
                    <th className="px-4 py-2 font-semibold">Available capacity</th>
                    <th className="px-4 py-2 font-semibold">NTD detail</th>
                    <th className="px-4 py-2 font-semibold">Data ports</th>
                    <th className="px-4 py-2 font-semibold">Mapping</th>
                    <th className="px-4 py-2 font-semibold text-right">Order</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#3c3836]">
                  {detail.ntds.map((n) => {
                    // On a transfer, the port to take is the one Vocus named in AVCIDTarget.
                    const targetPort = journey === 'TRANSFER' && avcVerification?.target
                      ? n.ports.find((x) => String(x.portNumber) === String(avcVerification.target))
                      : null;
                    const freePort = n.ports.find((x) => x.portType !== 'VOICE' && x.available === true);
                    const port = targetPort || (journey === 'TRANSFER' ? null : freePort);
                    const orderable = journey === 'TRANSFER' ? avcVerification?.match !== false : Boolean(freePort) || n.ports.length === 0;
                    return (
                      <tr key={n.ntdId || Math.random()} className="hover:bg-[#32302f]">
                        <td className="px-4 py-3 font-mono text-[#ebdbb2] whitespace-nowrap">{n.ntdId || '—'}</td>
                        <td className="px-4 py-3 text-[#b8bb26] whitespace-nowrap">
                          ↓ {fmtMbps(n.remainingDownload)} &nbsp; ↑ {fmtMbps(n.remainingUpload)}
                          {(n.downloadSpeed || n.uploadSpeed) && (
                            <div className="text-[11px] text-[#928374]">of ↓ {fmtMbps(n.downloadSpeed)} ↑ {fmtMbps(n.uploadSpeed)}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-[#a89984] whitespace-nowrap">
                          {n.ntdDetail || '—'}
                          {n.battery != null && <div className="text-[11px] text-[#928374]">Battery: {n.battery ? 'yes' : 'no'}</div>}
                        </td>
                        <td className="px-4 py-3">
                          {n.ports.filter((x) => x.portType !== 'VOICE').length > 0
                            ? n.ports.filter((x) => x.portType !== 'VOICE').map((x) => <PortPill key={`${n.ntdId}-${x.portNumber}`} port={x} />)
                            : <span className="text-[#928374]">—</span>}
                          {targetPort && <div className="text-[11px] text-[#83a598] mt-1">AVC runs on {targetPort.portName || `UNI-D${targetPort.portNumber}`}</div>}
                        </td>
                        <td className="px-4 py-3">
                          <select value={order.planMapping} onChange={(e) => updateOrder('planMapping', e.target.value)}
                            className="px-2 py-1 rounded bg-[#1d2021] border border-[#3c3836] text-[#ebdbb2] text-xs focus:outline-none focus:border-[#fabd2f]">
                            <option value="default">Default map</option>
                            <option value="tagged">Tagged map</option>
                          </select>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button type="button" disabled={!orderable} onClick={() => chooseNtd(n, port)}
                            className="px-3 py-1.5 rounded-lg bg-[#458588] hover:bg-[#83a598] text-[#ebdbb2] text-xs font-semibold whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed">
                            Reseller-Connect {detailTech || 'FTTP'}
                          </button>
                          {!orderable && <div className="text-[11px] text-[#928374] mt-1">No free data port</div>}
                        </td>
                      </tr>
                    );
                  })}

                  {/* Orphan ports — returned without a matching NTDRecord */}
                  {detail.orphanPorts.filter((x) => x.portType !== 'VOICE').length > 0 && (
                    <tr>
                      <td className="px-4 py-3 font-mono text-[#a89984]">{detail.orphanPorts[0].ntdId || 'unlisted NTD'}</td>
                      <td className="px-4 py-3 text-[#928374]">—</td>
                      <td className="px-4 py-3 text-[#928374]">—</td>
                      <td className="px-4 py-3">{detail.orphanPorts.filter((x) => x.portType !== 'VOICE').map((x) => <PortPill key={`orphan-${x.ntdId}-${x.portNumber}`} port={x} />)}</td>
                      <td className="px-4 py-3 text-[#928374]">—</td>
                      <td className="px-4 py-3 text-right text-[11px] text-[#928374]">No NTD record</td>
                    </tr>
                  )}

                  {/* NEW NTD — order a fresh NTD at the premise (vendor portal's second row) */}
                  {journey === 'NEW' && (
                    <tr className="hover:bg-[#32302f]">
                      <td className="px-4 py-3 text-[#ebdbb2] font-semibold">NEW NTD</td>
                      <td className="px-4 py-3 text-[#928374]">—</td>
                      <td className="px-4 py-3 text-[#928374]">Vocus/NBN install</td>
                      <td className="px-4 py-3 text-[#928374]">—</td>
                      <td className="px-4 py-3">
                        <select value={order.planMapping} onChange={(e) => updateOrder('planMapping', e.target.value)}
                          className="px-2 py-1 rounded bg-[#1d2021] border border-[#3c3836] text-[#ebdbb2] text-xs focus:outline-none focus:border-[#fabd2f]">
                          <option value="default">Default map</option>
                          <option value="tagged">Tagged map</option>
                        </select>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button type="button" onClick={() => chooseNtd(null, null, 'new')}
                          className="px-3 py-1.5 rounded-lg border border-[#504945] bg-[#3c3836] hover:bg-[#504945] text-[#ebdbb2] text-xs font-semibold whitespace-nowrap">
                          Reseller-Connect {detailTech || 'FTTP'}
                        </button>
                      </td>
                    </tr>
                  )}

                  {detail.ntds.length === 0 && journey === 'TRANSFER' && (
                    <tr>
                      <td colSpan={6} className="px-4 py-4 text-sm text-[#a89984]">
                        No NTD records returned for this premise — normal for FTTB/FTTN/FTTC, which transfer on a
                        copper pair rather than a UNI-D port.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-3 border-t border-[#3c3836] flex items-center justify-between flex-wrap gap-2">
              <span className="text-[11px] text-[#928374]">
                Ordering a router/CPE with the service isn't wired into this tool yet — the Vocus CPE params
                (<code>CPEPlanID</code>/<code>NBNCPEPlanID</code>) aren't sent.
              </span>
              <button type="button" onClick={() => chooseNtd(null, null, 'skip')}
                className="px-4 py-2 rounded-lg border border-[#504945] bg-[#3c3836] hover:bg-[#504945] text-[#ebdbb2] text-xs">
                Skip NTD selection → order details
              </button>
            </div>
          </div>

          {/* FTTB/N copper pairs */}
          {detail.copperPairs.length > 0 && (
            <div className="bg-[#282828] border border-[#3c3836] rounded-xl overflow-hidden">
              <div className="px-5 py-3 bg-[#1d2021] border-b border-[#3c3836]">
                <h2 className="text-sm font-semibold text-[#ebdbb2]">Copper pairs (FTTB/FTTN)</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wide text-[#a89984] border-b border-[#3c3836]">
                      <th className="px-4 py-2 font-semibold">Copper pair</th>
                      <th className="px-4 py-2 font-semibold">Status</th>
                      <th className="px-4 py-2 font-semibold">NBN service</th>
                      <th className="px-4 py-2 font-semibold">Speed (down/up)</th>
                      <th className="px-4 py-2 font-semibold">EPID</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#3c3836]">
                    {detail.copperPairs.map((c) => (
                      <tr key={c.copperPairId}>
                        <td className="px-4 py-2 font-mono text-[#ebdbb2]">{c.copperPairId || '—'}</td>
                        <td className="px-4 py-2 text-[#a89984]">{c.status || '—'}</td>
                        <td className="px-4 py-2 text-[#a89984]">{c.nbnServiceStatus || '—'}</td>
                        <td className="px-4 py-2 text-[#a89984]">{c.downloadSpeed || '—'} / {c.uploadSpeed || '—'} Mbps</td>
                        <td className="px-4 py-2 font-mono text-[#a89984]">{c.epid || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Step 4: Order Details ── */}
      {step === 'order' && (
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Loaded-customer banner — real provision against the record */}
          {loadedCustomer && (
            <div className="flex items-center gap-3 p-3 bg-[#fabd2f]/10 border border-[#fabd2f]/40 rounded-lg text-sm">
              <FaUser className="text-[#fabd2f] shrink-0" />
              <div className="min-w-0 flex-1">
                <span className="text-[#fabd2f] font-medium">Provisioning customer:</span>
                <span className="text-[#ebdbb2] ml-2">{order.customerName}</span>
                <span className="text-[#a89984] ml-2">{loadedCustomer.email}</span>
                <div className="text-[11px] text-[#a89984] mt-0.5">This submits a REAL FIBRE::Create and saves the order + PPPoE credentials to this customer's record.</div>
              </div>
              <button type="button" onClick={() => { clearLoadedCustomer(); }} className="text-xs text-[#83a598] hover:text-[#8ec07c] shrink-0">Unlink</button>
            </div>
          )}
          {/* QUALIFY summary bar */}
          {order.directoryId && (
            <div className="flex items-start gap-3 p-3 bg-emerald-900/20 border border-emerald-700/40 rounded-lg text-sm">
              <FaCheckCircle className="text-emerald-400 mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1">
                <span className="text-emerald-200 font-medium">QUALIFY passed</span>
                <span className="text-[#a89984] ml-2">LOC: <span className="font-mono text-[#ebdbb2]">{order.directoryId}</span></span>
                {order.csa && <span className="text-[#a89984] ml-2">CSA: <span className="font-mono text-[#ebdbb2]">{order.csa}</span></span>}
                {order.serviceType && <span className="text-[#a89984] ml-2">Tech: <span className="font-mono text-[#ebdbb2]">{order.serviceType}</span></span>}
              </div>
              <button type="button" onClick={() => setStep('qualify')} className="text-xs text-[#83a598] hover:text-[#8ec07c] shrink-0">Re-qualify</button>
            </div>
          )}
          {!order.directoryId && (
            <div className="flex items-center gap-2 p-3 bg-amber-900/20 border border-amber-700/40 rounded-lg text-sm text-amber-200">
              <FaInfoCircle className="shrink-0" />
              Manual entry mode — no QUALIFY run. Enter the LOC/DirectoryID manually below.
              <button type="button" onClick={() => setStep('qualify')} className="ml-auto text-xs text-[#83a598] hover:text-[#8ec07c] shrink-0">← Back to QUALIFY</button>
            </div>
          )}

          {renderCustomerIdentity()}

          {/* Customer details */}
          <div className="bg-[#282828] border border-[#3c3836] rounded-xl p-5 space-y-4">
            <SectionHeader title="Customer Details" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Customer name" v={order.customerName} on={(v) => updateOrder('customerName', v)} placeholder="Jane Smith" required />
              <Field label="Phone" v={order.phone} on={(v) => updateOrder('phone', v)} placeholder="0400000000" />
            </div>
          </div>

          {/* Credentials */}
          <div className="bg-[#282828] border border-[#3c3836] rounded-xl p-5 space-y-4">
            <SectionHeader
              title="PPPoE Credentials"
              subtitle="Leave the username blank — Vocus assigns the LOC-derived one itself (and appends its own trailing digits, e.g. 000192977184·11@peaknet.com.au). Password is 8 letters of the customer's name plus 3 random digits." />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="NBN Domain" v={NBN_REALM} on={() => {}} readOnly mono />
              <label className="block">
                <span className="flex items-center justify-between text-xs font-medium text-[#a89984] mb-1">
                  Customer Username
                  <button type="button" onClick={() => updateOrder('username', order.username ? '' : pppoeUsernameFromLoc(order.directoryId))}
                    disabled={!order.directoryId}
                    className="text-[11px] text-[#83a598] hover:text-[#8ec07c] disabled:opacity-40">
                    {order.username ? 'Clear (let Vocus assign)' : 'Force from LOC'}
                  </button>
                </span>
                <input
                  type="text"
                  value={order.username}
                  onChange={(e) => updateOrder('username', e.target.value)}
                  placeholder={order.directoryId ? `auto — ${pppoeUsernameFromLoc(order.directoryId)}` : 'auto — assigned by Vocus'}
                  className="w-full px-3 py-2 rounded-lg bg-[#1d2021] border border-[#3c3836] text-[#ebdbb2] focus:outline-none focus:border-[#fabd2f] font-mono text-sm"
                />
              </label>
              <label className="block">
                <span className="flex items-center justify-between text-xs font-medium text-[#a89984] mb-1">
                  <span>Customer Password<span className="text-[#fb4934] ml-0.5">*</span></span>
                  <button type="button" onClick={() => updateOrder('password', pppoePasswordFromName(order.customerName))}
                    disabled={!order.customerName.trim()}
                    className="text-[11px] text-[#83a598] hover:text-[#8ec07c] disabled:opacity-40">Generate</button>
                </span>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={order.password}
                    onChange={(e) => updateOrder('password', e.target.value)}
                    placeholder="e.g. JohnSmit482"
                    className="w-full px-3 py-2 rounded-lg bg-[#1d2021] border border-[#3c3836] text-[#ebdbb2] focus:outline-none focus:border-[#fabd2f] font-mono text-sm pr-16"
                  />
                  <div className="absolute right-1 top-1 flex gap-1">
                    <button type="button" onClick={() => setShowPassword(!showPassword)} title="Toggle visibility"
                      className="p-1.5 rounded text-[#a89984] hover:text-[#ebdbb2]">
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                    <button type="button" onClick={() => navigator.clipboard?.writeText(order.password)} title="Copy"
                      className="p-1.5 rounded text-[#a89984] hover:text-[#ebdbb2]">
                      <FaCopy />
                    </button>
                  </div>
                </div>
              </label>
            </div>
            <p className="text-[11px] text-[#928374]">
              Allowed password characters: A-z 0-9 - _ . · Vocus returns the password only once, so it's saved
              to the customer record on submit.
            </p>
          </div>

          {/* Plan */}
          <div className="bg-[#282828] border border-[#3c3836] rounded-xl p-5 space-y-4">
            <SectionHeader title="Service Plan" />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <SelectField label="Technology" v={order.serviceType} on={(v) => { updateOrder('serviceType', v); const tiers = speedTiersFor(normalizeTech(v)); updateOrder('speedTier', tiers.includes(order.speedTier) ? order.speedTier : ''); }}
                options={['FTTP','FTTB','FTTN','FTTC','HFC','WIRELESS']} required />
              <SelectField label="Speed tier" v={order.speedTier} on={(v) => updateOrder('speedTier', v)}
                options={[{ value: '', label: '— choose —' },
                  ...speedTiers.map((t) => ({ value: t, label: tierLabel(tech, t) }))]} required />
              <SelectField label="Mapping" v={order.planMapping} on={(v) => updateOrder('planMapping', v)}
                options={[{ value: 'default', label: 'Default (DM)' }, { value: 'tagged', label: 'Tagged (FP)' }]} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="Plan ID override (optional)" v={order.planId} on={(v) => updateOrder('planId', v)} placeholder="e.g. 5939-DM" mono />
              <SelectField label="Service level" v={order.serviceLevel} on={(v) => updateOrder('serviceLevel', v)}
                options={['STANDARD','ENHANCED4','ENHANCED6','ENHANCED8','ENHANCED12']} />
            </div>
            {loadedCustomer && order.customerPlanName && (
              <div className={`p-2.5 rounded-lg border text-[11px] ${
                order.speedTier
                  ? 'bg-[#98971a]/10 border-[#98971a]/40 text-[#b8bb26]'
                  : 'bg-[#cc241d]/10 border-[#cc241d]/50 text-[#fb4934]'}`}>
                {order.speedTier
                  ? <>Customer is on <strong>{order.customerPlanName}</strong> → Vocus speed tier <strong>{order.speedTier}</strong>.</>
                  : <>Customer is on <strong>{order.customerPlanName}</strong>, which has no <code>vocusSpeedTier</code> in the Plans catalogue — pick the tier yourself, or the order can&apos;t be resolved to a PlanID.</>}
              </div>
            )}
            <div className="flex items-center gap-2 flex-wrap p-2.5 rounded-lg bg-[#1d2021] border border-[#3c3836] text-xs">
              <span className="text-[#a89984]">Vocus will be sent</span>
              {resolvedPlanId
                ? <>
                    <span className="font-mono font-semibold text-[#fabd2f]">{resolvedPlanId}</span>
                    <span className="text-[#928374]">
                      — {tech || '?'} · {order.speedTier ? tierLabel(tech, order.speedTier) : 'no tier chosen'}
                      {order.planId.trim() ? ' (manual override)' : ''}
                    </span>
                  </>
                : <span className="text-[#fb4934]">no PlanID yet — pick a technology and speed tier</span>}
            </div>
            <p className="text-[11px] text-[#928374]">
              PlanID is auto-resolved from Technology + Speed tier + Mapping unless you enter an override. Peaknet uses Default (DM) for all residential services.
            </p>
          </div>

          {/* Connection */}
          <div className="bg-[#282828] border border-[#3c3836] rounded-xl p-5 space-y-4">
            <SectionHeader title="Connection Details" />
            {/* What the availability page decided — shown here so it's obvious what's being ordered. */}
            {/* "Current NTD" — the vendor portal's read-only summary of the device being ordered on. */}
            {(selectedNtd || detail) && (
              <div className="flex items-start justify-between gap-3 flex-wrap p-3 rounded-lg bg-[#1d2021] border border-[#3c3836] text-xs">
                <div className="text-[#a89984] space-y-0.5">
                  <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-[#928374]">
                    <FaNetworkWired className="text-[#83a598]" /> Current NTD
                  </div>
                  {selectedNtd?.isNewNtd ? (
                    <div className="text-[#fabd2f]">NEW NTD — ordering a new device at this premise (install required)</div>
                  ) : selectedNtd?.skipped ? (
                    <div className="text-[#a89984]">No NTD specified — Vocus picks the device and the free port.</div>
                  ) : selectedNtdRecord ? (
                    <>
                      <div className="font-mono text-[#ebdbb2]">
                        {selectedNtdRecord.ntdId} {selectedNtdRecord.ntdDetail || ''}
                      </div>
                      <div>{selectedNtdRecord.battery ? 'Battery installed.' : 'No Battery available.'}</div>
                      <div>
                        Remaining Capacity — ↓ {fmtMbps(selectedNtdRecord.remainingDownload)} ↑ {fmtMbps(selectedNtdRecord.remainingUpload)}
                      </div>
                      {selectedNtd?.portName && <div>UniD Port — <span className="font-mono text-[#ebdbb2]">{selectedNtd.portName}</span></div>}
                    </>
                  ) : selectedNtd?.ntdId ? (
                    <div className="font-mono text-[#ebdbb2]">{selectedNtd.ntdId}</div>
                  ) : (
                    <div>No NTD selected — Vocus picks the free port automatically.</div>
                  )}
                  {avcVerification?.match === true && <div className="text-[#b8bb26]">AVC verified against this LOC ✓</div>}
                  {avcVerification?.match === false && <div className="text-[#fb4934]">AVC NOT matched to this LOC ✕</div>}
                </div>
                {detail && (
                  <button type="button" onClick={() => setStep('availability')}
                    className="px-3 py-1.5 rounded-lg border border-[#504945] bg-[#3c3836] hover:bg-[#504945] text-[#ebdbb2]">
                    Back to availability
                  </button>
                )}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              <SelectField label="Order type" v={order.orderType} on={(v) => updateOrder('orderType', v)}
                options={['NEW','TRANSFER','OUTSTANDING']} />
              <Field label="AVCID (TRANSFER only)" v={order.avcid} on={(v) => updateOrder('avcid', v)} placeholder="AVC000245601980" mono />
              <SelectField label="UNI-D port (TRANSFER only)" v={order.dataPortNumber} on={(v) => updateOrder('dataPortNumber', v)}
                options={['Auto','1','2','3','4']} />
              <Field label="NTD ID (blank = Vocus picks)" v={order.ntdId} on={(v) => updateOrder('ntdId', v)} placeholder="NTD000073455877 or NEW" mono />
            </div>
            {order.orderType !== 'NEW' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="block">
                  <span className="block text-xs font-medium text-[#a89984] mb-1">
                    Customer Authority date<span className="text-[#fb4934] ml-0.5">*</span>
                  </span>
                  <input type="date" value={order.caDate ? `${order.caDate.slice(0,4)}-${order.caDate.slice(4,6)}-${order.caDate.slice(6,8)}` : ''}
                    onChange={(e) => updateOrder('caDate', e.target.value.replace(/-/g, ''))}
                    className="w-full px-3 py-2 rounded-lg bg-[#1d2021] border border-[#3c3836] text-[#ebdbb2] focus:outline-none focus:border-[#fabd2f]" />
                </label>
                <p className="text-[11px] text-[#928374] self-end pb-2">
                  The date the customer signed authority to transfer. Vocus requires it on every
                  TRANSFER/OUTSTANDING order (<code>CADate</code>) and rejects anything older than
                  45 days. Defaults to today.
                </p>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <SelectField label="IP Assignment" v={order.ipAssignment} on={(v) => updateOrder('ipAssignment', v)}
                options={['CGNAT','STATIC','DYNAMIC']} />
              <SelectField label="Auth type" v={order.authType} on={(v) => updateOrder('authType', v)}
                options={['PPPoE','IPoE']} />
              <Field label="Order reference (optional)" v={order.orderReference} on={(v) => updateOrder('orderReference', v)} placeholder="Customer ref…" />
            </div>
            <div className="space-y-1.5 text-[11px] text-[#928374]">
              <p><span className="text-[#a89984] font-medium">AVCID format:</span> use the <code className="text-[#fabd2f]">AVC</code>-prefixed NBN ID (e.g. <code className="text-[#fabd2f]">AVC000245601980</code>). Bare 12-digit values are auto-prefixed on submit.</p>
              <p className="flex items-start gap-1.5">
                <FaInfoCircle className="mt-0.5 shrink-0" />
                <span><span className="text-[#a89984] font-medium">Router / CPE:</span> this order provisions the NBN tail only — it does <span className="text-[#a89984]">not</span> order a modem. Vocus ships the default CPE bundled with the FIBRE PlanID (if any). Selecting a specific Vocus/NBN CPE (CPEPlanID / NBNCPEPlanID + shipment destination) is a separate flow we haven't built — Peaknet supplies its own router.</span>
              </p>
            </div>
          </div>

          {/* Address/LOC (from QUALIFY, editable) */}
          <div className="bg-[#282828] border border-[#3c3836] rounded-xl p-5 space-y-4">
            <SectionHeader title="NBN Location" subtitle="Auto-filled from QUALIFY. Edit only if needed." />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="LOC / DirectoryID" v={order.directoryId} on={(v) => updateOrder('directoryId', v)} placeholder="LOC000xxxxx" mono required />
              <Field label="CSA (optional)" v={order.csa} on={(v) => updateOrder('csa', v)} placeholder="CSA…" mono />
              <Field label="Premise postcode (LocationReference)"
                v={order.qualifyPostcode}
                on={(v) => updateOrder('qualifyPostcode', v.replace(/\D/g, '').slice(0, 4))}
                placeholder="4504" mono required />
            </div>
            {!/^\d{4}$/.test(order.qualifyPostcode.trim()) && (
              <div className="p-2.5 rounded-lg bg-[#cc241d]/10 border border-[#cc241d]/50 text-[11px] text-[#fb4934]">
                Vocus sends this as <code>LocationReference</code> and rejects the order without it
                (<code>FIBRE-4220</code>). It often can&apos;t be auto-filled — Vocus&apos;s address record
                frequently omits the postcode — so type the premise postcode. The CSA is not a substitute.
              </div>
            )}
          </div>

          {submitError && (
            <div className="p-4 rounded-lg bg-red-900/40 border border-red-700 text-red-200 text-sm flex items-start gap-2">
              <FaTimesCircle className="mt-0.5 shrink-0" /> {submitError}
            </div>
          )}

          <div className="flex gap-3">
            <button type="submit" disabled={submitting}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[#fabd2f] hover:bg-[#fe8019] text-[#1d2021] font-bold text-base disabled:opacity-50">
              <FaServer />{submitting ? 'Submitting…' : loadedCustomer ? 'Provision Customer (FIBRE Create)' : 'Submit FIBRE Create'}
            </button>
            <button type="button" onClick={() => setStep(detail ? 'availability' : 'qualify')}
              className="px-5 py-3 rounded-lg border border-[#504945] bg-[#3c3836] hover:bg-[#504945] text-[#ebdbb2]">
              ← Back
            </button>
          </div>
        </form>
      )}

      {/* ── Step 3: Result ── */}
      {step === 'result' && submitResult && (
        <div className="space-y-4">
          <div className={`p-5 rounded-xl border ${submitResult.transactionId || submitResult.vocusOrderSnapshot?.transactionId ? 'bg-emerald-900/30 border-emerald-700/60' : 'bg-amber-900/30 border-amber-700/60'}`}>
            <div className="flex items-center gap-2 mb-3">
              {(submitResult.transactionId || submitResult.vocusOrderSnapshot?.transactionId)
                ? <FaCheckCircle className="text-emerald-400 text-xl" />
                : <FaExclamationTriangle className="text-amber-400 text-xl" />}
              <h3 className="text-lg font-semibold text-[#ebdbb2]">
                {(submitResult.transactionId || submitResult.vocusOrderSnapshot?.transactionId) ? 'Order submitted successfully' : 'Order submitted — check status'}
              </h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              {[
                ['Transaction ID', submitResult.transactionId || submitResult.vocusOrderSnapshot?.transactionId],
                ['Service ID', submitResult.serviceId || submitResult.vocusOrderSnapshot?.serviceId],
                ['Status', submitResult.status || submitResult.vocusOrderSnapshot?.status],
                ['Vocus Tx State', submitResult.txState || submitResult.vocusOrderSnapshot?.txState],
                ['Username', submitResult.vocusPppoeUsername || submitResult.username],
                ['Password', submitResult.vocusPppoePassword || submitResult.password],
                ['Plan ID', submitResult.resolvedPlanId || submitResult.planId],
              ].filter(([, v]) => v != null && v !== '').map(([k, v]) => (
                <div key={k} className="flex justify-between gap-3 border-b border-[#3c3836]/50 py-1.5">
                  <span className="text-[#928374]">{k}</span>
                  <span className="text-[#ebdbb2] font-mono text-right">{String(v)}</span>
                </div>
              ))}
            </div>
          </div>

          <details className="bg-[#282828] border border-[#3c3836] rounded-xl">
            <summary className="px-4 py-2 cursor-pointer text-xs font-semibold uppercase tracking-wide text-[#a89984] bg-[#1d2021] border-b border-[#3c3836]">
              Raw response
            </summary>
            <pre className="p-4 text-[11px] text-[#ebdbb2] overflow-x-auto max-h-96">{JSON.stringify(submitResult, null, 2)}</pre>
          </details>

          <div className="flex gap-3">
            <button type="button" onClick={reset}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#fabd2f] hover:bg-[#fe8019] text-[#1d2021] font-semibold">
              <FaRedo /> Provision another
            </button>
            <button type="button" onClick={() => setStep('order')}
              className="px-5 py-2.5 rounded-lg border border-[#504945] bg-[#3c3836] hover:bg-[#504945] text-[#ebdbb2]">
              ← Back to order
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
